<template>
  <b-modal
    id="date-modal"
    :title="title"
    modal-class="modal-primary modal-cesar-empleado"
    hide-footer
    @hidden="$emit('close')"
    size="md"
    scrollable
    v-model="showModal"
    centered
    no-close-on-backdrop
    no-close-on-esc
  >
    <b-form @submit.prevent="continuar">
      <!-- Fecha de cese -->
      <b-form-group label="Fecha de cese" label-for="fecha-cese">
        <b-form-datepicker
          id="fecha-cese"
          v-model="form.fecha"
          :min="minDate"
          :max="maxDate"
          class="w-100"
          placeholder="Selecciona la fecha de cese"
          container="body"
          locale="es-PE"
          :date-format-options="{
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }"
        />
      </b-form-group>

      <!-- Motivo de cese -->
      <b-form-group label="Motivo de cese" label-for="motivo-cese">
        <b-form-select
          id="motivo-cese"
          v-model="form.motivo_cese_id"
          :options="motivosOptions"
          class="w-100"
        />
        <small
          v-if="selectedMotivo && selectedMotivo.genera_indemnizacion"
          class="text-warning d-block mt-1 font-weight-bold"
        >
          <feather-icon icon="AlertTriangleIcon" size="14" class="mr-25" />
          Este motivo genera indemnización en la liquidación.
        </small>
      </b-form-group>

      <!-- Fecha fin de contrato (solo si el motivo lo requiere) -->
      <b-form-group
        v-if="requiereFinContrato"
        label="Fecha fin de contrato pactada"
        label-for="fecha-fin-contrato"
        description="Fecha en que vencía el contrato (posterior al cese) para calcular la indemnización."
      >
        <b-form-datepicker
          id="fecha-fin-contrato"
          v-model="form.fecha_fin_contrato"
          :min="minFinContrato"
          class="w-100"
          placeholder="Selecciona la fecha fin de contrato"
          container="body"
          locale="es-PE"
          :date-format-options="{
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }"
        />
      </b-form-group>

      <div class="d-flex justify-content-end align-items-center mt-2">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          @click="showModal = false"
          :disabled="isSubmitting"
        >
          Cancelar
        </b-button>
        <b-button
          variant="danger"
          :disabled="!isFormValid || isSubmitting"
          @click="continuar"
        >
          <b-spinner small v-if="isSubmitting" class="mr-50" />
          Guardar Cese
        </b-button>
      </div>
    </b-form>
  </b-modal>
</template>

<script>
import employeeService from "@/views/rrhh/views/employees/services/employee.service.js";

export default {
  props: {
    modalData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showModal: true,
      isSubmitting: false,
      form: {
        fecha: null,
        motivo_cese_id: 1, // Por defecto Renuncia voluntaria
        fecha_fin_contrato: null,
      },
      motivos: [
        { id: 1, codigo: "renuncia", nombre: "Renuncia voluntaria", genera_indemnizacion: 0, requiere_fecha_fin_contrato: 0 },
        { id: 2, codigo: "vencimiento_contrato", nombre: "Vencimiento del contrato", genera_indemnizacion: 0, requiere_fecha_fin_contrato: 0 },
        { id: 3, codigo: "mutuo_disenso", nombre: "Mutuo disenso", genera_indemnizacion: 0, requiere_fecha_fin_contrato: 0 },
        { id: 4, codigo: "despido_arbitrario", nombre: "Despido arbitrario", genera_indemnizacion: 1, requiere_fecha_fin_contrato: 0 },
        { id: 5, codigo: "termino_anticipado_plazo_fijo", nombre: "Término anticipado de contrato a plazo fijo", genera_indemnizacion: 1, requiere_fecha_fin_contrato: 1 },
        { id: 6, codigo: "falta_grave", nombre: "Despido por falta grave comprobada", genera_indemnizacion: 0, requiere_fecha_fin_contrato: 0 },
        { id: 7, codigo: "periodo_prueba", nombre: "Término en periodo de prueba", genera_indemnizacion: 0, requiere_fecha_fin_contrato: 0 },
        { id: 8, codigo: "fallecimiento", nombre: "Fallecimiento", genera_indemnizacion: 0, requiere_fecha_fin_contrato: 0 },
        { id: 9, codigo: "jubilacion", nombre: "Jubilación", genera_indemnizacion: 0, requiere_fecha_fin_contrato: 0 },
      ],
    };
  },
  async created() {
    await this.cargarMotivos();
  },
  computed: {
    title() {
      return `Cese de ${this.modalData.full_name}`;
    },
    minDate() {
      return null;
    },
    maxDate() {
      return new Date().toISOString().split("T")[0];
    },
    minFinContrato() {
      return this.form.fecha || null;
    },
    selectedMotivo() {
      return this.motivos.find((m) => m.id == this.form.motivo_cese_id);
    },
    selectedMotivoName() {
      return this.selectedMotivo ? this.selectedMotivo.nombre : "";
    },
    requiereFinContrato() {
      return Boolean(this.selectedMotivo && this.selectedMotivo.requiere_fecha_fin_contrato);
    },
    motivosOptions() {
      return this.motivos.map((m) => ({
        value: m.id,
        text: m.nombre + (m.genera_indemnizacion ? " (Con indemnización)" : ""),
      }));
    },
    isFormValid() {
      if (!this.form.fecha || !this.form.motivo_cese_id) return false;
      if (this.requiereFinContrato) {
        if (!this.form.fecha_fin_contrato || this.form.fecha_fin_contrato <= this.form.fecha) {
          return false;
        }
      }
      return true;
    },
  },
  methods: {
    async cargarMotivos() {
      try {
        const { data } = await employeeService.getMotivosCese();
        if (Array.isArray(data) && data.length > 0) {
          this.motivos = data;
        }
      } catch (error) {
        console.warn("No se pudieron cargar los motivos de la API, usando lista base", error);
      }
    },
    async cesarEmpleado() {
      this.isSubmitting = true;
      try {
        const params = {
          empleado_id: this.modalData.id,
          fecha: this.form.fecha,
          motivo_cese_id: this.form.motivo_cese_id,
        };
        if (this.requiereFinContrato && this.form.fecha_fin_contrato) {
          params.fecha_fin_contrato = this.form.fecha_fin_contrato;
        }
        const { data } = await employeeService.cesarEmpleado(params);
        this.showToastSuccess(data.message || "Cese registrado exitosamente.");
        this.$emit("saved");
      } catch (error) {
        console.log(error);
        this.showToastError(error.response?.data);
      } finally {
        this.isSubmitting = false;
      }
    },
    async continuar() {
      if (!this.isFormValid) return;
      const motivoDesc = this.selectedMotivoName ? ` con motivo "${this.selectedMotivoName}"` : "";
      const { isConfirmed } = await this.showConfirmSwal(
        `¿Estás seguro de cesar a ${this.modalData.full_name} con fecha ${this.form.fecha}${motivoDesc}?`,
        "Esta acción no se puede deshacer y cerrará el vínculo laboral del trabajador."
      );
      if (isConfirmed) {
        this.cesarEmpleado();
      }
    },
  },
};
</script>

<style lang="scss">
.modal-cesar-empleado {
  .modal-body {
    overflow: visible !important;
  }

  .dropdown-menu,
  .b-calendar,
  .bv-calendar,
  .b-form-datepicker + .dropdown-menu {
    z-index: 3000 !important;
  }
}
</style>
